import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import type {
  FeedbackCategory,
  FeedbackSentiment,
  UserFeedbackRecord,
} from "../ChatRecord";

interface SubmitFeedbackParams {
  chatId: string;
  /** Org the chat belongs to. Sent as ``X-Roboto-Org-Id`` so
   *  ``REQUIRES_CALLER_ORG`` has an org to pin on for multi-org callers.
   *  Required at this layer (the underlying service throws on missing
   *  orgId; passing ``undefined`` here would only defer that throw to the
   *  service call). */
  chatOrgId: string;
  messageSequenceNum: number;
  sentiment: FeedbackSentiment;
  categories: FeedbackCategory[];
  /** Free-text notes. Use ``undefined`` (or omit) for "no notes"; the
   *  service layer normalises that to the wire ``null``. ``null`` is
   *  accepted equivalently for caller convenience but ``undefined`` is the
   *  preferred representation here. */
  notes?: string | null;
}

/**
 * Submit categorized feedback on an assistant message.
 *
 * On success, invalidates the ``["chat-feedback", chatId]`` query so the UI
 * reflects the updated rating state immediately.
 */
export function useSubmitChatFeedback() {
  const { chatService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<UserFeedbackRecord, Error, SubmitFeedbackParams>({
    mutationFn: async ({
      chatId,
      chatOrgId,
      messageSequenceNum,
      sentiment,
      categories,
      notes,
    }: SubmitFeedbackParams) => {
      return chatService.submitFeedback(
        chatId,
        messageSequenceNum,
        { sentiment, categories, notes },
        { orgId: chatOrgId },
      );
    },
    onSuccess: async (_record, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["chat-feedback", variables.chatId],
      });
    },
  });
}
